import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Typography, Box, Chip, IconButton} from 'views/components';
import {Translate, translate} from 'views/module';

import {VolumeUp, Stop} from 'views/icons';

const LanguageView = (props) => {
  const {bioQuestions, setIsComplete, caregiverRn} = props;

  const {
    languages = [],
  } = bioQuestions;

  const audioRef = React.useRef({});

  const [recordings, setRecordings] = React.useState([]);

  React.useEffect(() => {
    const nextRecordings = languages
        .filter((language) => Boolean(language.interview?.callReferenceId))
        .map((language) => {
          const callRefId = language.interview.callReferenceId;
          const previous = recordings.find((recording) => recording.language === language.name);

          return {
            language: language.name,
            url: `https://d3lfj9nx46seqe.cloudfront.net/public/caregiver/${caregiverRn}/interviews/${callRefId}.mp3`,
            playing: previous?.playing || false,
            hasError: previous?.hasError || false,
          };
        });

    setRecordings(nextRecordings);

    // checking if file exists, if error accessing it, set hasError to true, rendering play button invisible
    nextRecordings.forEach(({url}) => {
      const probeAudio = new Audio();
      probeAudio.preload = 'metadata';
      probeAudio.onerror = () => {
        setRecordings((prev) =>
          prev.map((recording) => recording.url === url ? {...recording, hasError: true} : recording),
        );
      };
      probeAudio.src = url;
    });
  }, [languages]);

  const handlePlayRecordingToggle = async (url) => {
    if (!audioRef.current[url]) {
      audioRef.current[url] = new Audio(url);
      audioRef.current[url].addEventListener('ended', () => {
        setRecordings((prev) =>
          prev.map((recording) => recording.url === url ? {...recording, playing: false} : recording),
        );
      });
    }

    const audio = audioRef.current[url];
    const recording = recordings.find((recording) => recording.url === url);
    if (!recording || recording.hasError) {
      return;
    }

    const isPlaying = !!recording?.playing;

    if (isPlaying) {
      audio.pause();
      audio.currentTime = 0;
    } else {
      Object.values(audioRef.current).forEach((audio) => {
        audio.pause();
        audio.currentTime = 0;
      });
      await audio.play();
    }

    setRecordings((prev) =>
      prev.map((recording) => recording.url === url ? {...recording, playing: !isPlaying} : {...recording, playing: false}),
    );
  };

  const buildPlayButton = (lang) => {
    const recording = recordings.find((x) => x.language === lang.name);
    if (!recording || recording.hasError) {
      return null;
    }

    const url = recording.url;
    const playing = recording.playing;
    return (
      <IconButton onClick={() => handlePlayRecordingToggle(url)}>
        {playing ? <Stop sx={{color: '#0288d1'}}/> : <VolumeUp sx={{color: '#0288d1'}}/>}
      </IconButton>
    );
  };

  React.useEffect(() => {
    const isComplete = languages.length > 0;
    setIsComplete(isComplete);
  }, [languages, setIsComplete]);

  if (!languages.length) {
    return (
      <Stack spacing={1.5} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          <Translate expression="caregiver.CaregiverBiography.Languages.EmptyState" />
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} alignItems="flex-start">
      <Box display="flex" flexWrap="wrap">
        {
          languages.map((language) => {
            const predefinedLanguage = translate(`caregiver.CaregiverBiography.Languages.AvailableLanguages.${language.name}`);
            const languageLabel = predefinedLanguage !== 'Missing translation' ? predefinedLanguage : language.name;

            const callRefId = language.interview?.callReferenceId || null;
            const hasCallRef = Boolean(callRefId);

            return (
              <Chip
                key={`${language.name}-${language.fluency}`}
                icon={hasCallRef ? buildPlayButton(language) : undefined}
                label={
                  <Typography
                    variant="body2"
                    sx={{color: '#0288d1', fontWeight: 500}}
                  >
                    {
                      `${languageLabel} : ${translate(`caregiver.CaregiverBiography.Languages.Fluency.${language.fluency}`)}`
                    }
                  </Typography>
                }
                sx={{
                  border: '1px solid #0288d1',
                  borderRadius: '16px',
                  backgroundColor: '#e1f5fe',
                  paddingY: '6px',
                  paddingX: '10px',
                  marginRight: '6px',
                  marginBottom: '6px',
                }}
              />
            );
          })
        }
      </Box>
    </Stack>
  );
};

LanguageView.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  setIsComplete: PropTypes.func.isRequired,
  caregiverRn: PropTypes.string.isRequired,
};

export default React.memo(LanguageView);
